import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Apple,
  Snowflake,
  Thermometer,
  Wind,
  Headphones,
  MapPin,
  ClipboardList,
  CalendarCheck,
  Radio,
  PackageCheck,
} from "lucide-react";
import { InnerPage } from "@/components/InnerPage";
import { SectionHeader } from "@/components/SectionHeader";
import { mergeHead, pageHead } from "@/lib/seo";
import { breadcrumbsFor, jsonLdScript, serviceJsonLd } from "@/lib/schema";
import { SITE } from "@/lib/site";

const PATH = "/refrigerated-freight";

const META_DESCRIPTION =
  "Temperature-controlled produce and perishables hauling coast to coast from a New Jersey carrier since 2012. Book reefer freight with Fox Trucking.";

const SERVICE_DESCRIPTION =
  "Temperature-controlled refrigerated trucking for fresh produce and perishable freight, hauled coast to coast by Fox Trucking LLC from New Jersey.";

export const Route = createFileRoute("/refrigerated-freight")({
  head: () =>
    mergeHead(
      pageHead({
        title: "Refrigerated & Produce Trucking | Fox Trucking NJ",
        description: META_DESCRIPTION,
        path: PATH,
        ogTitle: "Refrigerated & Produce Trucking | Fox Trucking NJ",
        ogDescription: META_DESCRIPTION,
      }),
      {
        scripts: [
          jsonLdScript(
            serviceJsonLd({
              name: "Refrigerated & Produce Freight",
              description: SERVICE_DESCRIPTION,
              path: PATH,
              serviceType: "Temperature-controlled refrigerated trucking",
              idSuffix: "service",
            }),
          ),
        ],
      },
    ),
  component: RefrigeratedFreightPage,
});

const HAUL_CARDS = [
  {
    i: Apple,
    t: "Fresh produce",
    d: "Time-sensitive fruits and vegetables that need the right temp from dock to destination — scheduled tightly, handled carefully.",
  },
  {
    i: Snowflake,
    t: "Perishable freight",
    d: "Chilled and frozen loads that can’t sit and wait. We treat every degree as part of the delivery promise.",
  },
  {
    i: Thermometer,
    // TODO: Confirm additional reefer commodity categories Fox Trucking regularly hauls.
    t: "Other reefer freight",
    d: "[OTHER REEFER COMMODITIES - placeholder]. Tell us the product and setpoint — we’ll confirm if it’s a fit.",
  },
] as const;

const PROTECT_STEPS = [
  {
    i: Wind,
    t: "Pre-cooled trailers",
    // TODO: Confirm pre-cool SOP and any customer-facing guarantees.
    d: "Trailers are brought to setpoint before loading so your product doesn’t warm up on the dock. [PRE-COOL PROCESS — confirm details].",
  },
  {
    i: Thermometer,
    t: "Temperature monitoring",
    // TODO: Confirm monitoring hardware / platform name and reporting cadence.
    d: "[TEMP MONITORING SYSTEM] — we track reefer performance in transit and flag issues before they become claims.",
  },
  {
    i: Headphones,
    t: "24/7 dispatch updates",
    d: "Humans answer around the clock. Need an ETA, a temp check, or a re-route? Call and someone picks up.",
  },
  {
    i: Snowflake,
    t: "Reefer capacity",
    // TODO: Confirm active reefer trailer count / fleet size for public copy.
    d: "Dedicated temperature-controlled capacity for produce and perishables. [TRAILER COUNT].",
  },
] as const;

const PROCESS_STEPS = [
  {
    i: ClipboardList,
    n: "01",
    t: "Request a quote",
    d: "Share origin, destination, commodity, weight, and required temperature. We’ll respond with a clear rate.",
  },
  {
    i: CalendarCheck,
    n: "02",
    t: "Confirm pickup",
    d: "Lock the appointment, equipment type, and setpoint. You get a driver and trailer assigned — not a guessing game.",
  },
  {
    i: Radio,
    n: "03",
    t: "Monitored transit",
    d: "Your load moves under temperature control with dispatch available 24/7 if plans change mid-route.",
  },
  {
    i: PackageCheck,
    n: "04",
    t: "On-time delivery",
    d: "We aim for the window you booked. Updates before you have to chase them.",
  },
] as const;

const REEFER_FAQS = [
  {
    question: "What temperature ranges can you hold?",
    // TODO: Confirm continuous setpoint ranges Fox Trucking will commit to publicly.
    answer:
      "[TEMPERATURE RANGE — confirm continuous setpoints with operations]. Share your required setpoint when you request a quote and we’ll confirm equipment fit.",
  },
  {
    question: "Do you haul fresh produce coast to coast?",
    answer: `Yes. Fresh produce is a core part of what we move. ${SITE.shortName} runs temperature-controlled freight from our New Jersey base across the lower 48.`,
  },
  {
    question: "How do you monitor reefer temperatures in transit?",
    // TODO: Confirm monitoring system name and whether shippers receive live access.
    answer:
      "[TEMP MONITORING SYSTEM — confirm platform and shipper reporting]. Dispatch stays reachable 24/7 if you need a status check.",
  },
  {
    question: "Can you also move dry van freight?",
    answer:
      "Yes. When your freight doesn’t need refrigeration, see our dry van and general freight service for FTL dry goods.",
  },
  {
    question: "How do I book a refrigerated load?",
    answer: `Call dispatch at ${SITE.phoneDisplay} or use the Book a Load page with pickup, delivery, commodity, and temperature requirements. We quote from real people — not a chatbot.`,
  },
];

function RefrigeratedFreightPage() {
  return (
    <InnerPage
      hero={{
        eyebrow: "Reefer · Produce · NJ",
        title: "Refrigerated & Produce Freight",
        intro:
          "Produce and perishables don’t get second chances. We haul temperature-controlled freight with the care, scheduling, and phone support your load deserves — coast to coast from New Jersey.",
        primaryCta: { label: "Book a Load", href: "/contact" },
        secondaryCta: { label: "Call Dispatch", href: `tel:${SITE.phoneTel}` },
        showPhoneCta: false,
      }}
      breadcrumbs={breadcrumbsFor("Refrigerated Freight", PATH)}
      faqs={REEFER_FAQS}
      faqTitle="Reefer freight questions."
      faqSubtitle="Straight answers for shippers booking temperature-controlled loads."
      bottomCta={{
        title: "Ready to move refrigerated freight?",
        subtitle:
          "Share commodity, weight, and temperature needs — a live dispatcher answers day or night.",
        bookLabel: "Book a Load",
        bookHref: "/contact",
      }}
    >
      {/* WHAT WE HAUL */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Commodities"
            title="What we haul."
            subtitle="Temperature-sensitive freight is the job — from field-fresh produce to other chilled and frozen product."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {HAUL_CARDS.map((c) => (
              <div
                key={c.t}
                className="group relative overflow-hidden rounded-lg border border-border bg-card p-8 transition hover:border-primary"
              >
                <c.i className="h-10 w-10 text-primary" aria-hidden="true" />
                <h3 className="mt-6 font-display text-2xl text-card-foreground">{c.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-sm text-muted-foreground">
            Need dry van instead? See our{" "}
            <Link
              to="/dry-van-freight"
              className="font-display tracking-wider text-primary underline-offset-2 hover:underline"
            >
              dry van &amp; general freight
            </Link>{" "}
            service for FTL loads that don’t require refrigeration.
          </p>
        </div>
      </section>

      {/* HOW WE PROTECT YOUR LOAD */}
      <section className="bg-muted py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Protection"
            title="How we protect your load."
            subtitle="Cold chain discipline plus dispatchers who actually answer — because a silent phone doesn’t save a load."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {PROTECT_STEPS.map((s) => (
              <div
                key={s.t}
                className="rounded-lg border border-border bg-card p-8 transition hover:border-primary"
              >
                <div className="grid h-12 w-12 place-items-center rounded-md bg-primary text-primary-foreground">
                  <s.i className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="mt-5 font-display text-xl text-card-foreground">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LANES */}
      <section className="relative bg-secondary py-24 text-secondary-foreground">
        <div className="absolute inset-0 bg-noise opacity-60" />
        <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <SectionHeader
                tone="dark"
                eyebrow="Coverage"
                title="Lanes that cross the country."
                subtitle={`Based in ${SITE.locationShort} since ${SITE.foundingYear}, we move refrigerated freight across the lower 48 — long-haul produce and perishables that need a carrier who shows up on time.`}
              />
              <p className="mt-6 max-w-2xl text-sm text-secondary-foreground/75">
                For a fuller picture of where we run, see our{" "}
                <Link
                  to="/coverage"
                  className="font-display tracking-wider text-accent underline-offset-2 hover:underline"
                >
                  coverage map and service area
                </Link>
                . Need dry goods on the return or a parallel lane? Pair this with{" "}
                <Link
                  to="/dry-van-freight"
                  className="font-display tracking-wider text-accent underline-offset-2 hover:underline"
                >
                  dry van freight
                </Link>
                .
              </p>
            </div>
            <div className="rounded-lg border border-accent/40 bg-secondary/60 p-8">
              <MapPin className="h-8 w-8 text-accent" aria-hidden="true" />
              <h3 className="mt-4 font-display text-2xl text-accent">New Jersey base</h3>
              <p className="mt-2 text-sm text-secondary-foreground/80">
                Coast-to-coast refrigerated hauling with 24/7 dispatch at{" "}
                <a href={`tel:${SITE.phoneTel}`} className="text-accent hover:underline">
                  {SITE.phoneDisplay}
                </a>
                .
              </p>
              <Link
                to="/coverage"
                className="mt-6 inline-flex h-12 items-center justify-center rounded-md bg-accent px-5 font-display tracking-wider text-accent-foreground hover:bg-accent/90"
              >
                View coverage
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="How it works"
            title="From quote to delivery."
            subtitle="Four clear steps — so your produce isn’t waiting on a mystery process."
          />
          <ol className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {PROCESS_STEPS.map((step) => (
              <li
                key={step.n}
                className="relative rounded-lg border border-border bg-card p-6 transition hover:border-primary"
              >
                <div className="flex items-center justify-between">
                  <step.i className="h-8 w-8 text-primary" aria-hidden="true" />
                  <span className="font-display text-2xl text-accent">{step.n}</span>
                </div>
                <h3 className="mt-5 font-display text-xl text-card-foreground">{step.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{step.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </InnerPage>
  );
}
